<template>
  <div class="betreuer-container">
    <!-- Admin-Bereich: PUT-Bereich für SamAccountName + MaxProjekte -->
    <div v-if="isAdmin" class="put-section">
      <label for="samInput">Lehrer:</label>
      <input
        id="samInput"
        type="text"
        v-model="manualSamAccountName"
        class="filter-input"
        placeholder="SamAccount"
      />

      <label for="capacityInput">Maximale Projekte:</label>
      <input
        id="capacityInput"
        type="number"
        v-model.number="manualMaxProjekte"
        class="filter-input"
        placeholder="0-3"
        min="0"
        max="3"
      />

      <button @click="putCapacityRequest" class="capacity-button">
        Speichern
      </button>
    </div>

    <!-- Lehrer-Bereich: Eigene Kapazität setzen (nur Lehrer, nicht Admin) -->
    <div v-if="isTeacher && !isAdmin" class="put-section">
      <label for="selfCapacityInput">Eigene maximale Projekte:</label>
      <input
        id="selfCapacityInput"
        type="number"
        v-model.number="selfMaxProjekte"
        class="filter-input"
        placeholder="0-3"
        min="0"
        max="3"
      />
      <button @click="updateSelfCapacity" class="capacity-button">
        Speichern
      </button>
    </div>

    <!-- Filter-Bereich (links: Dropdown + Suchfeld, rechts: Export-Buttons) -->
    <div class="filters">
      <div class="left-filters">
        <div class="entries-wrapper">
          <label>Zeige</label>
          <select v-model.number="itemsPerPage" class="entries-select">
            <option value="5">5</option>
            <option value="10">10</option>
            <option value="25">25</option>
            <option :value="-1">Alle</option>
          </select>
          <span>Einträge</span>
        </div>
        <div class="search-wrapper">
          <span class="material-symbols-outlined search-icon">search</span>
          <input
            v-model="search"
            placeholder="Suchbegriff"
            class="search-input"
            @input="fetchBetreuer"
          />
        </div>
      </div>
      <div class="actions-section">
        <div class="export-buttons">
          <button @click="downloadCSV" class="export-button">CSV</button>
          <button @click="downloadXLSX" class="export-button">XLSX</button>
          <button @click="downloadPDF" class="export-button">PDF</button>
        </div>
      </div>
    </div>

    <!-- Betreuerliste (Tabellen-Ansicht) -->
    <table v-if="betreuerList.length" class="betreuer-table">
      <thead>
        <tr>
          <th>ID</th>
          <th>Nachname</th>
          <th>Vorname</th>
          <th>Email</th>
          <th>Status</th>
          <th>Zugewiesene Projekte</th>
          <th>Maximale Projekte</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="betreuer in paginatedBetreuerList" :key="betreuer.id">
          <td>{{ betreuer.id }}</td>
          <td>{{ betreuer.nachname }}</td>
          <td>{{ betreuer.vorname }}</td>
          <td>
            <a :href="`mailto:${betreuer.email}`">{{ betreuer.email }}</a>
          </td>
          <td>{{ betreuer.status }}</td>
          <td>{{ betreuer.vergebeneProjekte ?? 0 }}</td>
          <td>{{ betreuer.maxProjekte }}</td>
        </tr>
      </tbody>
    </table>
    <p v-else class="no-projects">
      Keine Betreuer verfügbar. Bitte aktualisieren.
    </p>

    <!-- PAGINATION -->
    <div v-if="totalPages > 1" class="pagination">
      <button @click="goToPrevPage" :disabled="currentPage === 1">Letztes</button>
      <button
        v-for="page in pages"
        :key="page"
        @click="changePage(page)"
        :class="['page-button', { 'active-page': page === currentPage }]"
      >
        {{ page }}
      </button>
      <button @click="goToNextPage" :disabled="currentPage === totalPages">Nächstes</button>
    </div>

    <p v-if="errorMessage" class="error-message">{{ errorMessage }}</p>

    <!-- MODAL für Bearbeitung bleibt unverändert -->
    <div v-if="showEditBetreuerModal" class="modal-overlay">
      <div class="modal">
        <span class="close" @click="closeEditBetreuerModal">&times;</span>
        <h2>Betreuer bearbeiten</h2>
        <form @submit.prevent="updateBetreuer" class="modal-form">
          <label>Nachname:</label>
          <input type="text" v-model="editBetreuer.nachname" disabled />
          <label>Vorname:</label>
          <input type="text" v-model="editBetreuer.vorname" disabled />
          <label>Email:</label>
          <input type="text" v-model="editBetreuer.email" disabled />
          <label>Status:</label>
          <input type="text" v-model="editBetreuer.status" disabled />
          <label>Zugewiesene Projekte:</label>
          <input type="number" v-model="editBetreuer.vergebeneProjekte" disabled />
          <label>Maximale Projekte (0-3):</label>
          <input
            type="number"
            v-model.number="editBetreuer.maxProjekte"
            min="0"
            max="3"
            required
          />
          <button type="submit" class="submit-button">Speichern</button>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, computed } from "vue";
import { useAuthenticationStore } from "@/stores/useAuthStore";

function isAdminRole(role) {
  return role === "ROLE_ADMIN";
}
function canEditCapacityRole(role) {
  return role === "ROLE_ADMIN" || role === "ROLE_TEACHER";
}

export default {
  name: "Betreuer",
  setup() {
    const authStore = useAuthenticationStore();

    const betreuerList = ref([]);
    const errorMessage = ref(null);
    const search = ref("");
    const itemsPerPage = ref(5);
    const currentPage = ref(1);

    const manualSamAccountName = ref("");
    const manualMaxProjekte = ref(0);
    const selfMaxProjekte = ref(0);

    const showEditBetreuerModal = ref(false);
    const editBetreuer = ref({
      id: null,
      nachname: "",
      vorname: "",
      email: "",
      status: "",
      vergebeneProjekte: 0,
      maxProjekte: 0,
      samAccountName: ""
    });

    const csrfToken = ref(null);
    const csrfHeaderName = ref(null);

    async function getCsrfToken() {
      const response = await fetch(`${process.env.VUE_APP_BACKEND}/auth/csrf-token`, {
        method: "GET",
        credentials: "include"
      });
      if (!response.ok) {
        throw new Error("CSRF-Token konnte nicht abgerufen werden.");
      }
      const data = await response.json();
      csrfToken.value = data.token;
      csrfHeaderName.value = data.headerName;
    }

    onMounted(async () => {
      await authStore.checkAuth();
      fetchBetreuer();
    });

    const isAdmin = computed(() => isAdminRole(authStore.highestRole));
    const isTeacher = computed(() => authStore.highestRole === "ROLE_TEACHER");
    const canEditCapacity = computed(() => canEditCapacityRole(authStore.highestRole));

    const filteredBetreuerList = computed(() => {
      if (!search.value) return betreuerList.value;
      const term = search.value.toLowerCase();
      return betreuerList.value.filter((b) => {
        return (
          b.nachname.toLowerCase().includes(term) ||
          b.vorname.toLowerCase().includes(term) ||
          b.email.toLowerCase().includes(term)
        );
      });
    });
    const totalPages = computed(() => {
      if (itemsPerPage.value < 0) return 1;
      return Math.ceil(filteredBetreuerList.value.length / itemsPerPage.value);
    });
    const paginatedBetreuerList = computed(() => {
      if (itemsPerPage.value < 0) return filteredBetreuerList.value;
      const startIndex = (currentPage.value - 1) * itemsPerPage.value;
      return filteredBetreuerList.value.slice(startIndex, startIndex + itemsPerPage.value);
    });
    const pages = computed(() => {
      const arr = [];
      for (let i = 1; i <= totalPages.value; i++) {
        arr.push(i);
      }
      return arr;
    });

    function changePage(page) {
      currentPage.value = page;
    }
    function goToPrevPage() {
      if (currentPage.value > 1) currentPage.value--;
    }
    function goToNextPage() {
      if (currentPage.value < totalPages.value) currentPage.value++;
    }

    async function fetchBetreuer() {
      try {
        errorMessage.value = null;
        const resp = await fetch(`${process.env.VUE_APP_BACKEND}/diplomdb/api/betreuer`, {
          method: "GET",
          credentials: "include"
        });
        if (!resp.ok) {
          throw new Error("fetchBetreuer nicht ok, Status=" + resp.status);
        }
        const data = await resp.json();
        betreuerList.value = data;
        if (currentPage.value > totalPages.value) currentPage.value = 1;
      } catch (error) {
        alert("FEHLER: " + error.message);
      }
    }

    async function putCapacityRequest() {
      try {
        if (manualSamAccountName.value.trim() === "") {
          alert("Bitte SamAccountName eingeben.");
          return;
        }
        if (manualMaxProjekte.value < 0 || manualMaxProjekte.value > 3) {
          alert("MaxProjekte muss zwischen 0 und 3 liegen.");
          return;
        }
        await getCsrfToken();
        const url = `${process.env.VUE_APP_BACKEND}/diplomdb/api/betreuer/sam/${encodeURIComponent(manualSamAccountName.value)}/capacity?maxProjekte=${manualMaxProjekte.value}`;
        const response = await fetch(url, {
          method: "PUT",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            [csrfHeaderName.value]: csrfToken.value
          }
        });
        if (!response.ok) {
          throw new Error(`PUT war nicht ok, Status=${response.status}`);
        }
        alert("Kapazität erfolgreich geändert!");
        manualSamAccountName.value = "";
        manualMaxProjekte.value = 0;
        fetchBetreuer();
      } catch (error) {
        alert("FEHLER: " + error.message);
      }
    }

    async function updateSelfCapacity() {
      try {
        if (selfMaxProjekte.value < 0 || selfMaxProjekte.value > 3) {
          alert("MaxProjekte muss zwischen 0 und 3 liegen.");
          return;
        }
        await getCsrfToken();
        const url = `${process.env.VUE_APP_BACKEND}/diplomdb/api/betreuer/capacity/self?maxProjekte=${selfMaxProjekte.value}`;
        const response = await fetch(url, {
          method: "PUT",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            [csrfHeaderName.value]: csrfToken.value
          }
        });
        if (!response.ok) {
          throw new Error(`PUT war nicht ok, Status=${response.status}`);
        }
        alert("Eigenkapazität erfolgreich geändert!");
        selfMaxProjekte.value = 0;
        fetchBetreuer();
      } catch (error) {
        alert("FEHLER: " + error.message);
      }
    }

    function openEditBetreuerModal(b) {
      editBetreuer.value = { ...b };
      showEditBetreuerModal.value = true;
    }
    function closeEditBetreuerModal() {
      showEditBetreuerModal.value = false;
      editBetreuer.value = {
        id: null,
        nachname: "",
        vorname: "",
        email: "",
        status: "",
        vergebeneProjekte: 0,
        maxProjekte: 0,
        samAccountName: ""
      };
    }
    async function updateBetreuer() {
      try {
        if (editBetreuer.value.maxProjekte < 0 || editBetreuer.value.maxProjekte > 3) {
          alert("MaxProjekte muss zwischen 0 und 3 liegen.");
          return;
        }
        await getCsrfToken();
        const url = `${process.env.VUE_APP_BACKEND}/diplomdb/api/betreuer/sam/${encodeURIComponent(editBetreuer.value.samAccountName)}/capacity?maxProjekte=${editBetreuer.value.maxProjekte}`;
        const resp = await fetch(url, {
          method: "PUT",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            [csrfHeaderName.value]: csrfToken.value
          }
        });
        if (!resp.ok) {
          throw new Error("PUT war nicht ok, Status=" + resp.status);
        }
        alert("MaxProjekte erfolgreich geändert.");
        closeEditBetreuerModal();
        fetchBetreuer();
      } catch (error) {
        alert("FEHLER: " + error.message);
      }
    }

    function downloadCSV() {
      downloadExport("csv", "betreuer_liste.csv");
    }
    function downloadXLSX() {
      downloadExport("xlsx", "betreuer_liste.xlsx");
    }
    function downloadPDF() {
      downloadExport("pdf", "betreuer_liste.pdf");
    }
    async function downloadExport(format, filename) {
      try {
        const url = `${process.env.VUE_APP_BACKEND}/diplomdb/api/betreuer/export?format=${format}&search=${search.value}&sortField=id&sortDirection=asc`;
        const response = await fetch(url, {
          method: "GET",
          credentials: "include"
        });
        if (!response.ok) {
          throw new Error("Netzwerkantwort war nicht ok");
        }
        const blob = await response.blob();
        const link = document.createElement("a");
        const urlBlob = URL.createObjectURL(blob);
        link.href = urlBlob;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (error) {
        alert("FEHLER: " + error.message);
      }
    }

    return {
      authStore,
      betreuerList,
      errorMessage,
      search,
      itemsPerPage,
      currentPage,
      manualSamAccountName,
      manualMaxProjekte,
      selfMaxProjekte,
      showEditBetreuerModal,
      editBetreuer,
      isAdmin,
      isTeacher,
      canEditCapacity,
      filteredBetreuerList,
      totalPages,
      paginatedBetreuerList,
      pages,
      changePage,
      goToPrevPage,
      goToNextPage,
      fetchBetreuer,
      putCapacityRequest,
      updateSelfCapacity,
      openEditBetreuerModal,
      closeEditBetreuerModal,
      updateBetreuer,
      downloadCSV,
      downloadXLSX,
      downloadPDF,
      downloadExport
    };
  }
};
</script>

<style scoped>
.betreuer-container {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-height: 90vh;
  font-family: 'DM Sans', sans-serif;
  padding: 20px;
  max-width: 1100px;
  margin: 0 auto;
}
button {
  background-color: #5841ee;
  border: none;
  color: #fff;
  padding: 8px 12px;
  border-radius: 4px;
  cursor: pointer;
  font-weight: bold;
  font-family: 'DM Sans', sans-serif;
  transition: background-color 0.3s;
  outline: none;
}
button:hover {
  background-color: #4a35d2;
}
button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.put-section {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  margin-bottom: 20px;
  gap: 10px;
}
.filter-input {
  padding: 8px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 14px;
  font-family: 'DM Sans', sans-serif;
}
.filters {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 65%;
  margin-bottom: 20px;
}
.left-filters {
  display: flex;
  align-items: center;
  gap: 20px;
}
.entries-wrapper {
  display: flex;
  align-items: center;
  gap: 6px;
}
.entries-select {
  padding: 4px;
  border-radius: 4px;
  border: 1px solid #ccc;
  font-family: 'DM Sans', sans-serif;
  cursor: pointer;
}
.search-wrapper {
  position: relative;
  display: flex;
  width: 300px;
}
.search-icon {
  position: absolute;
  left: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: #666;
  font-size: 20px;
  pointer-events: none;
}
.search-input {
  width: 100%;
  padding: 8px 8px 8px 35px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 14px;
  font-family: 'DM Sans', sans-serif;
}
.actions-section {
  display: flex;
  align-items: center;
  gap: 10px;
}
.export-buttons {
  display: flex;
  gap: 10px;
}
.betreuer-table {
  width: 100%;
  border-collapse: collapse;
  border-radius: 8px;
  overflow: hidden;
  font-size: 14px;
  background-color: #fff;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}
.betreuer-table thead tr {
  background-color: #f4f4f4;
}
.betreuer-table th,
.betreuer-table td {
  padding: 16px;
  text-align: center;
  border-bottom: 1px solid #ddd;
  font-family: 'DM Sans', sans-serif;
}
.betreuer-table tbody tr:nth-child(odd) {
  background-color: #F7F6FE;
}
.betreuer-table tbody tr:nth-child(even) {
  background-color: #ffffff;
}
.betreuer-table tbody tr:hover {
  background-color: #eaeafc;
}
.no-projects {
  text-align: center;
  margin: 20px 0;
  font-size: 16px;
  color: #777;
  font-family: 'DM Sans', sans-serif;
}
.error-message {
  text-align: center;
  margin-top: 20px;
  font-size: 16px;
  color: red;
  font-family: 'DM Sans', sans-serif;
}
.pagination {
  margin-top: 20px;
  display: flex;
  align-items: center;
  gap: 6px;
  font-family: 'DM Sans', sans-serif;
}
.pagination button {
  padding: 6px 12px;
  border-radius: 4px;
  cursor: pointer;
  background-color: #e1e1e1;
  color: #000;
  font-weight: normal;
  transition: background-color 0.3s;
  border: none;
}
.pagination button:hover {
  background-color: #d4d4d4;
}
.pagination button:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}
.page-button.active-page {
  background-color: #5841ee;
  color: #fff;
  font-weight: bold;
}
.page-button.active-page:hover {
  background-color: #4a35d2;
}
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0,0,0,0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
}
.modal {
  background-color: #fff;
  padding: 20px;
  border-radius: 6px;
  min-width: 350px;
  max-width: 500px;
  position: relative;
  font-family: 'DM Sans', sans-serif;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}
.close {
  font-size: 24px;
  font-weight: bold;
  position: absolute;
  right: 15px;
  top: 10px;
  cursor: pointer;
}
.modal-form {
  display: flex;
  flex-direction: column;
}
.modal-form label {
  margin-top: 10px;
  font-weight: bold;
  font-family: 'DM Sans', sans-serif;
}
.modal-form input {
  padding: 8px;
  margin-top: 5px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-family: 'DM Sans', sans-serif;
}
.submit-button {
  margin-top: 20px;
  background-color: #5841ee;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 10px;
  cursor: pointer;
  font-family: 'DM Sans', sans-serif;
  transition: background-color 0.3s;
}
.submit-button:hover {
  background-color: #4a35d2;
}
</style>
